import { useEffect, useRef, useState } from 'react';

import { Modal } from './Modal';
import { Button } from './Button';
import {
  RICH_TEXT_IMAGE_MAX_BYTES,
  RICH_TEXT_IMAGE_MAX_WIDTH,
  uploadRichTextImage,
} from '../../services/firebase';

/**
 * Pick an image for the rich-text editor to embed, either by uploading one or
 * by pointing at a URL that is already public.
 *
 * Both paths are offered because both are real: a curator writing about a watch
 * usually has the image on disk, but the ones already in our own Storage — a
 * brand hero, a reference packshot — are a URL away and re-uploading them would
 * put a second copy of the same bytes in the bucket.
 *
 * What the modal returns is a `src` and an `alt`, not an `<img>`. Building the
 * node is the editor's job; this component's job is to make sure the src is one
 * the RN client can actually load, which is why an uploaded file is not
 * considered chosen until the upload has finished and resolved a download URL.
 */

export interface ImageSelection {
  src: string;
  alt: string;
}

export interface ImagePickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (selection: ImageSelection) => void;
  /**
   * Populated when an existing image is selected in the editor, which turns
   * this into an edit rather than an insert. The URL tab opens on it, so
   * changing only the alt text does not mean finding the image again.
   */
  initial?: ImageSelection;
  /**
   * The tab to open on. Defaults to URL when `initial` has a src (editing an
   * embedded image), else Upload. A "replace this image" control passes
   * 'upload', since replacing usually means uploading a copy.
   */
  initialTab?: 'upload' | 'url';
}

type Tab = 'upload' | 'url';

/**
 * Alt text worth defaulting to, derived from the filename: `sub-hero_2024.jpg`
 * becomes `sub hero 2024`. It is a starting point and not an answer — the
 * curator is expected to overwrite it — but it beats an empty field, which is
 * what most images would otherwise ship with.
 */
export function altFromFileName(fileName: string): string {
  const withoutExtension = fileName.replace(/\.[a-zA-Z0-9]{1,5}$/, '');
  return withoutExtension.replace(/[-_]+/g, ' ').replace(/\s+/g, ' ').trim();
}

/**
 * Whether a pasted URL is one an RN client can load. `http:` is allowed rather
 * than blocked so an image on a staging host still previews, but it is called
 * out — App Transport Security will refuse it on a real device.
 */
export function describeUrlProblem(url: string): string | null {
  const trimmed = url.trim();
  if (!trimmed) return null;

  let parsed: URL;
  try {
    parsed = new URL(trimmed);
  } catch {
    return 'That does not look like a full URL — include the https:// prefix.';
  }

  if (parsed.protocol === 'http:') {
    return 'This is an http:// URL. The app will refuse to load it on a device — use https:// if the host offers it.';
  }
  if (parsed.protocol !== 'https:') {
    return `The app can only load http and https images, not ${parsed.protocol.replace(':', '')}.`;
  }
  return null;
}

const TABS: { id: Tab; label: string }[] = [
  { id: 'upload', label: 'Upload' },
  { id: 'url', label: 'Paste URL' },
];

export function ImagePickerModal({
  isOpen,
  onClose,
  onSelect,
  initial,
  initialTab,
}: ImagePickerModalProps) {
  const [tab, setTab] = useState<Tab>('upload');
  const [src, setSrc] = useState('');
  const [alt, setAlt] = useState('');
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [previewFailed, setPreviewFailed] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Reset on each open rather than on close, so a modal that is closed
  // mid-upload does not clear the fields under the upload still running.
  useEffect(() => {
    if (!isOpen) return;
    setSrc(initial?.src ?? '');
    setAlt(initial?.alt ?? '');
    setTab(initialTab ?? (initial?.src ? 'url' : 'upload'));
    setUploading(false);
    setProgress(0);
    setError(null);
    setNote(null);
    setPreviewFailed(false);
    // `initial` is a fresh object on every render of the parent, so depending on
    // it would reset the fields on every keystroke the parent re-renders for.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleFile = async (file: File) => {
    setUploading(true);
    setError(null);
    setNote(null);
    setProgress(0);
    setPreviewFailed(false);
    try {
      const result = await uploadRichTextImage(file, {
        onProgress: setProgress,
      });
      setSrc(result.downloadUrl);
      // Only fill the alt if the curator has not written one — an upload that
      // replaces a previously chosen image should not throw away their words.
      setAlt((current) => current || altFromFileName(file.name));
      if (result.resized) {
        setNote(
          `Resized from ${result.originalWidth}px wide to ${RICH_TEXT_IMAGE_MAX_WIDTH}px before upload.`,
        );
      }
    } catch (err) {
      console.error('Failed to upload rich-text image:', err);
      setError(err instanceof Error ? err.message : 'Failed to upload the image');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const urlProblem = tab === 'url' ? describeUrlProblem(src) : null;
  const trimmedSrc = src.trim();
  const canInsert = trimmedSrc.length > 0 && !uploading;

  const insert = () => {
    if (!canInsert) return;
    onSelect({ src: trimmedSrc, alt: alt.trim() });
  };

  const limitMb = Math.round(RICH_TEXT_IMAGE_MAX_BYTES / (1024 * 1024));

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Insert Image" size="lg">
      <div className="space-y-4">
        <div className="flex gap-1 border-b border-gray-200">
          {TABS.map((entry) => (
            <button
              key={entry.id}
              type="button"
              onClick={() => setTab(entry.id)}
              className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                tab === entry.id
                  ? 'border-brand-600 text-brand-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {entry.label}
            </button>
          ))}
        </div>

        {tab === 'upload' ? (
          <div className="space-y-2">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              aria-label="Image file"
              disabled={uploading}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) void handleFile(file);
              }}
              className="block w-full text-sm text-gray-700 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200"
            />
            <p className="text-xs text-gray-500">
              Up to {limitMb} MB. Anything wider than {RICH_TEXT_IMAGE_MAX_WIDTH}px is
              scaled down before upload — the app renders these into a phone-width
              column.
            </p>
            {uploading && (
              <div className="space-y-1">
                <div className="h-1.5 bg-gray-200 rounded overflow-hidden">
                  <div
                    className="h-full bg-brand-600 transition-all"
                    style={{ width: `${progress}%` }}
                  />
                </div>
                <p className="text-xs text-gray-500">Uploading… {progress}%</p>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-2">
            <label
              className="block text-sm font-medium text-gray-700"
              htmlFor="rich-text-image-url"
            >
              Image URL
            </label>
            <input
              id="rich-text-image-url"
              type="url"
              value={src}
              onChange={(e) => {
                setSrc(e.target.value);
                setPreviewFailed(false);
              }}
              placeholder="https://…"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            />
            {urlProblem && <p className="text-xs text-amber-700">{urlProblem}</p>}
          </div>
        )}

        <div className="space-y-2">
          <label
            className="block text-sm font-medium text-gray-700"
            htmlFor="rich-text-image-alt"
          >
            Alt text{' '}
            <span className="font-normal text-gray-400">
              (optional, but it is what a screen reader reads in place of the image)
            </span>
          </label>
          <input
            id="rich-text-image-alt"
            type="text"
            value={alt}
            onChange={(e) => setAlt(e.target.value)}
            placeholder="Describe the image"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
          />
        </div>

        {error && (
          <p className="text-sm text-red-600" role="alert">
            {error}
          </p>
        )}
        {note && <p className="text-xs text-gray-500">{note}</p>}

        {trimmedSrc && (
          <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
            <p className="text-xs font-medium text-gray-500 mb-2">Preview</p>
            {previewFailed ? (
              <p className="text-sm text-red-600">
                That URL did not load as an image. It can still be inserted, but the
                app will not be able to show it either.
              </p>
            ) : (
              <img
                src={trimmedSrc}
                alt={alt || 'Preview'}
                onError={() => setPreviewFailed(true)}
                className="max-h-64 max-w-full mx-auto rounded"
              />
            )}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2 border-t border-gray-200">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={insert} disabled={!canInsert}>
            {initial?.src ? 'Update' : 'Insert'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
